import React from "react";
import { motion } from "framer-motion";
import {
  Broom, Sparkle, House, Buildings, Truck, ListChecks,
  ShieldCheck, Leaf, HeartStraight, Clock, MapPin, Quotes, DeviceMobile, Check,
} from "@phosphor-icons/react";
import { useLang } from "../../context/LanguageContext";
import { usePWAInstall } from "../../hooks/usePWAInstall";
import { TRUST_IMG, OFFICE_IMG } from "../../lib/assets";
import { toast } from "sonner";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

function Overline({ children }) {
  return <span className="text-xs font-bold uppercase tracking-[0.3em] text-leaf">{children}</span>;
}

/* ---------------------------------------------------------------- Marquee */
export function Marquee() {
  const { t } = useLang();
  const items = [...t.marquee, ...t.marquee];
  return (
    <div className="relative overflow-hidden border-y border-forest/10 bg-forest py-5" data-testid="marquee">
      <div className="flex w-max animate-marquee gap-10 whitespace-nowrap">
        {items.map((m, i) => (
          <span key={i} className="flex items-center gap-10 font-serif text-2xl italic text-white/90">
            {m} <Sparkle size={18} weight="fill" className="text-gold" />
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Services */
const serviceIcons = [Broom, Sparkle, House, Buildings, Truck, ListChecks];

export function Services() {
  const { t } = useLang();
  return (
    <section id="services" className="relative py-24 sm:py-32 bg-sage">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl">
          <Overline>{t.services.overline}</Overline>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-forest">{t.services.title}</h2>
          <p className="mt-4 text-forest/70 text-lg">{t.services.sub}</p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {t.services.items.map((s, i) => {
            const Icon = serviceIcons[i % serviceIcons.length];
            return (
              <motion.div
                key={s.n}
                custom={i}
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -6 }}
                className="group relative rounded-2xl bg-white border border-forest/10 p-7 transition-shadow hover:shadow-xl"
                data-testid={`service-card-${i}`}
              >
                <div className="flex items-start justify-between">
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-forest/5 text-forest group-hover:bg-forest group-hover:text-white transition-colors">
                    <Icon size={24} weight="duotone" />
                  </div>
                  <span className="font-serif text-3xl text-forest/15">{s.n}</span>
                </div>
                <h3 className="mt-6 font-serif text-2xl text-forest">{s.t}</h3>
                <p className="mt-2 text-forest/65 leading-relaxed">{s.d}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Why choose us */
const whyIcons = [ShieldCheck, Leaf, HeartStraight, Clock];

export function WhyChooseUs() {
  const { t } = useLang();
  return (
    <section id="why" className="relative py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }} className="relative">
            <div className="rounded-[2rem] overflow-hidden ring-1 ring-forest/10 shadow-2xl">
              <img src={TRUST_IMG} alt="Équipe de nettoyage" className="w-full h-[460px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-2 sm:right-6 rounded-2xl bg-gold px-6 py-5 text-white shadow-xl max-w-[200px]">
              <div className="font-serif text-2xl leading-tight">{t.why.tagline}</div>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-7">
          <Overline>{t.why.overline}</Overline>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-forest">{t.why.title}</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {t.why.items.map((w, i) => {
              const Icon = whyIcons[i];
              return (
                <motion.div
                  key={i}
                  custom={i}
                  variants={reveal}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="rounded-2xl border border-forest/10 bg-sage p-6"
                  data-testid={`why-card-${i}`}
                >
                  <div className="grid h-11 w-11 place-items-center rounded-full bg-forest text-white">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-forest">{w.t}</h3>
                  <p className="mt-1.5 text-sm text-forest/65 leading-relaxed">{w.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Areas */
export function Areas() {
  const { t } = useLang();
  return (
    <section id="areas" className="relative py-24 sm:py-32 bg-forest text-white overflow-hidden grain">
      <div className="absolute -top-32 right-0 h-96 w-96 rounded-full bg-leaf/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Overline>{t.areas.overline}</Overline>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight">{t.areas.title}</h2>
          <p className="mt-5 text-white/70 text-lg max-w-md">{t.areas.sub}</p>
          <div className="mt-8 rounded-2xl overflow-hidden ring-1 ring-white/15">
            <img src={OFFICE_IMG} alt="Bureau propre" className="w-full h-56 object-cover" />
          </div>
        </div>
        <div className="lg:col-span-7 flex flex-wrap gap-3 content-start lg:pt-16">
          {t.areas.list.map((a, i) => (
            <motion.span
              key={a}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-3 text-base font-semibold hover:bg-white hover:text-forest transition-colors"
              data-testid={`area-${i}`}
            >
              <MapPin size={16} weight="fill" className="text-gold" /> {a}
            </motion.span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Testimonials */
export function Testimonials() {
  const { t } = useLang();
  return (
    <section className="py-24 sm:py-32 bg-sage">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <Overline>{t.testimonials.overline}</Overline>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-forest">{t.testimonials.title}</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {t.testimonials.items.map((tm, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="rounded-2xl bg-white border border-forest/10 p-8"
              data-testid={`testimonial-${i}`}
            >
              <Quotes size={36} weight="fill" className="text-leaf/40" />
              <p className="mt-4 font-serif text-xl italic text-forest leading-relaxed">&laquo;&nbsp;{tm.q}&nbsp;&raquo;</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-full bg-forest text-white font-bold">{tm.a[0]}</div>
                <div>
                  <div className="font-bold text-forest text-sm">{tm.a}</div>
                  <div className="text-xs text-forest/55">{tm.c}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Download App */
export function DownloadApp() {
  const { t } = useLang();
  const { installed, promptInstall } = usePWAInstall();

  const handleInstall = async () => {
    const res = await promptInstall();
    if (res === "unavailable") toast.info(t.app.unavailable);
    else if (res === "accepted") toast.success(t.app.installed);
  };

  return (
    <section className="py-24 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-forest px-8 sm:px-16 py-16 grain">
          <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-gold/20 blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Overline>{t.app.overline}</Overline>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-white">{t.app.title}</h2>
              <p className="mt-5 text-white/75 text-lg max-w-md">{t.app.sub}</p>
              <button
                onClick={handleInstall}
                disabled={installed}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-bold text-white hover:bg-gold-dark transition-colors disabled:opacity-70"
                data-testid="pwa-install-btn"
              >
                {installed ? <Check size={18} weight="bold" /> : <DeviceMobile size={18} weight="bold" />}
                {installed ? t.app.installed : t.app.install}
              </button>
            </div>
            <div className="flex justify-center lg:justify-end">
              <div className="relative w-52 h-96 rounded-[2.2rem] bg-black/30 ring-1 ring-white/20 p-3 shadow-2xl">
                <div className="h-full w-full rounded-[1.6rem] bg-sage grid place-items-center overflow-hidden">
                  <div className="text-center px-4">
                    <div className="grid h-16 w-16 mx-auto place-items-center rounded-2xl bg-forest text-white">
                      <Broom size={30} weight="duotone" />
                    </div>
                    <div className="mt-4 font-serif text-2xl text-forest leading-tight">Kelsy</div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-leaf font-bold">Housecleaning</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
